import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './SubAssemblyLive.module.css';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { buildErrorMessage, getSubAssemblyConfigurations, getMaterialDroppingUpdates } from '../../apis/calls';
import { SCREEN_REFRESH_INTERVAL_TIME_MS } from '../../constants';
import SubAssemblyLiveToolbar from '../SubAssemblyLiveToolbar/SubAssemblyLiveToolbar';
import SubAssemblyTable from '../SubAssemblyTable/SubAssemblyTable';
import SubAssemblyFooter from '../SubAssemblyFooter/SubAssemblyFooter';

const SubAssemblyLive = () => {
  const location = useLocation();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;
  const [selectedLine, setSelectedLine] = useState(location?.state?.selectedLine || []);
  const [activeAsnDate, setActiveAsnDate] = useState(null);
  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);
  const [subAssemblyConfigs, setSubAssemblyConfigs] = useState([]);

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
  });
  const [countData, setCountData] = useState({
    loading: false,
    data: {},
  });

  if (selectedLine === '') {
    setSelectedLine(line);
  }

  const loadSubAssemblyData = async (
    selectedPlantValue = plant.value,
    selectedChassisLineValue = line.value,
  ) => {
    if (!selectedPlantValue || !selectedChassisLineValue) return;
    try {
      setRowsData(prevState => ({ ...prevState, loading: true }));
      const [configRes, readyDroppingRes, droppedRes] = await Promise.all([
        getSubAssemblyConfigurations(selectedPlantValue, selectedChassisLineValue).catch(() => ({ data: [] })),
        getMaterialDroppingUpdates(selectedPlantValue, selectedChassisLineValue, 'READY_FOR_DROP', 60).catch(() => ({ data: [] })),
        getMaterialDroppingUpdates(selectedPlantValue, selectedChassisLineValue, 'DROPPED', 100).catch(() => ({ data: [] })),
      ]);

      if (configRes && configRes.data && Array.isArray(configRes.data)) {
        setSubAssemblyConfigs(configRes.data);
      } else {
        setSubAssemblyConfigs([]);
      }

      const readyRows =
        readyDroppingRes && readyDroppingRes.data && Array.isArray(readyDroppingRes.data)
          ? readyDroppingRes.data
          : [];

      const droppedRows =
        droppedRes && droppedRes.data && Array.isArray(droppedRes.data)
          ? droppedRes.data
          : [];

      const combinedRows = [...readyRows, ...droppedRows].map((r, i) => ({
        rowIndex: i + 1,
        ...r,
      }));

      setRowsData({
        loading: false,
        data: combinedRows,
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      setRowsData(prevState => ({ ...prevState, loading: false }));
    }
  };

  useEffect(() => {
    loadSubAssemblyData(plant.value, line.value);

    const intervalId = setInterval(
      () => loadSubAssemblyData(plant.value, line.value),
      SCREEN_REFRESH_INTERVAL_TIME_MS,
    );

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAsnDate, plant.value, line.value, selectedLine]);

  return (
    <div className={styles.display}>
      <div className={styles.header}>
        <SubAssemblyLiveToolbar
          activeDate={activeAsnDate}
          setActiveDate={setActiveAsnDate}
          plant={plant}
          line={line}
          selectedOptionalColNames={selectedOptionalColNames}
          handleColumnSelection={selection =>
            setSelectedOptionalColNames(selection)
          }
        />
      </div>
      <div className={styles.tableArea}>
        <SubAssemblyTable
          rows={rowsData.data}
          selectedOptionalColNames={selectedOptionalColNames}
          countData={countData}
          subAssemblyConfigs={subAssemblyConfigs}
        />
      </div>
      <div className={styles.footer}>
        <SubAssemblyFooter
          asns={rowsData.data}
          countData={countData}
        />
      </div>
    </div>
  );
};

export default SubAssemblyLive;
